import { navigate, graphql } from 'gatsby';
import React, { useEffect, useState, useRef } from 'react';

import Seo from 'gatsby-plugin-wpgraphql-seo';
import { InlineWidget } from "react-calendly";
import Layout from '~/components/Layout';
import Image from '~/components/atoms/Image';
import CustomSEO from '~/components/seo/CustomSEO';
import { pricingResultsLabels } from '~/data/translations/pricingFormLabels';// import { StaticImage } from 'gatsby-plugin-image';

import './pricing-result.css';
import { StaticImage } from 'gatsby-plugin-image';

import { Chart as ChartJS, BarController, BarElement, LineElement, CategoryScale, LinearScale, PointElement, Legend, Tooltip } from 'chart.js';
import { Chart, Bar, Line } from 'react-chartjs-2';

ChartJS.register(BarController, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Legend, Tooltip);
const CHECK_DARK_SVG =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10.5" fill="#12203A" stroke="#12203A" stroke-width="3"/>
      <path d="M6 11L9.79289 14.7929C10.1834 15.1834 10.8166 15.1834 11.2071 14.7929L18 8" stroke="white" stroke-width="3" stroke-linecap="round"/>
    </svg>
  `);

const CHECK_LIGHT_SVG =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10.5" fill="white" stroke="white" stroke-width="3"/>
      <path d="M6 11L9.79289 14.7929C10.1834 15.1834 10.8166 15.1834 11.2071 14.7929L18 8" stroke="#12203A" stroke-width="3" stroke-linecap="round"/>
    </svg>
  `);

const CROSS_SVG =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M18.0001 5.99997C17.8125 5.8125 17.5582 5.70718 17.2931 5.70718C17.0279 5.70718 16.7736 5.8125 16.5861 5.99997L12.0001 10.586L7.41406 5.99997C7.22653 5.8125 6.97223 5.70718 6.70706 5.70718C6.4419 5.70718 6.18759 5.8125 6.00006 5.99997C5.81259 6.1875 5.70728 6.44181 5.70728 6.70697C5.70728 6.97213 5.81259 7.22644 6.00006 7.41397L10.5861 12L6.00006 16.586C5.81259 16.7735 5.70728 17.0278 5.70728 17.293C5.70728 17.5581 5.81259 17.8124 6.00006 18C6.18759 18.1874 6.4419 18.2928 6.70706 18.2928C6.97223 18.2928 7.22653 18.1874 7.41406 18L12.0001 13.414L16.5861 18C16.7736 18.1874 17.0279 18.2928 17.2931 18.2928C17.5582 18.2928 17.8125 18.1874 18.0001 18C18.1875 17.8124 18.2928 17.5581 18.2928 17.293C18.2928 17.0278 18.1875 16.7735 18.0001 16.586L13.4141 12L18.0001 7.41397C18.1875 7.22644 18.2928 6.97213 18.2928 6.70697C18.2928 6.44181 18.1875 6.1875 18.0001 5.99997Z" fill="white"/>
    </svg>
  `);

const rows = [
  'Short-term lets',
  'Guest Vetting',
  'Hassle-Free',
  '24/7 Guest Comms',
  'Maintenance',
  'Pricing Algorithm',
  'Multiple Sites',
  'Tech Stack',
  'Local Teams',
  'Multidisciplinary Team',
];


export default function PricingResult(props) {
  const state = props?.location?.state ?? null;
  const wpPage = props?.data?.wpPage ?? null;

  // derive values from state only after it's defined
  const sendToEmail = Boolean(state?.sendToEmail);
  const submissionId = state?.submissionId
    ?? state?.formSubmissionId
    ?? state?.estimateId
    ?? state?.id
    ?? null;
  const chartRef = useRef(null);
  const [chartReady, setChartReady] = useState(false);
  const generateAndSendPdf = async () => {
    try {
      if (typeof window === "undefined") return;

      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import(/* webpackChunkName: "html2canvas" */ "html2canvas"),
        import(/* webpackChunkName: "jspdf" */ "jspdf"),
      ]);

      const source = document.getElementById("pdfContent");
      if (!source) throw new Error("#pdfContent not found");

      // ---- Clone off-screen at a fixed desktop width so layout is stable
      const DESKTOP_WIDTH = 1280;
      const EXTRA_BOTTOM = 120; // ensure the very bottom (button) isn't clipped
      const CHART_TARGET_W = 900;
      const UPSCALE = 3;

      const clone = source.cloneNode(true);
      Object.assign(clone.style, {
        display: "block",
        visibility: "visible",
        background: "#fff",
        boxSizing: "border-box",
        overflow: "visible",
        maxWidth: "none",
        width: `${DESKTOP_WIDTH}px`,
        paddingBottom: `${EXTRA_BOTTOM}px`,
      });
      clone.querySelectorAll(".max-w-5xl, #chart-container, #chart").forEach(el => {
        el.style.maxWidth = "none";
        el.style.width = "100%";
        el.style.marginLeft = "auto";
        el.style.marginRight = "auto";
      });

      // Replace live chart <canvas> with a sharp image so it renders at full quality
      const liveCanvas = document.querySelector("#chart canvas");
      const cloneChartCanvas = clone.querySelector("#chart canvas") || clone.querySelector("canvas");
      if (liveCanvas && cloneChartCanvas) {
        const cssW = liveCanvas.offsetWidth || liveCanvas.width || 600;
        const cssH = liveCanvas.offsetHeight || liveCanvas.height || 340;
        const aspect = cssH / cssW || 0.6;

        const off = document.createElement("canvas");
        off.width = Math.max(1, Math.round(CHART_TARGET_W * UPSCALE));
        off.height = Math.max(1, Math.round(CHART_TARGET_W * aspect * UPSCALE));
        const ctx = off.getContext("2d");
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(liveCanvas, 0, 0, off.width, off.height);

        const img = document.createElement("img");
        img.src = off.toDataURL("image/png");
        img.style.width = `${CHART_TARGET_W}px`;
        img.style.height = `${Math.round(CHART_TARGET_W * aspect)}px`;
        img.style.display = "block";
        img.style.margin = "0 auto";
        cloneChartCanvas.replaceWith(img);
      }

      // Off-screen sandbox so the clone fully renders
      const sandbox = document.createElement("div");
      Object.assign(sandbox.style, {
        position: "fixed",
        left: "-100000px",
        top: "0",
        background: "#ffffff",
        zIndex: "-1",
        width: `${DESKTOP_WIDTH}px`,
      });
      sandbox.appendChild(clone);
      document.body.appendChild(sandbox);

      // Wait for fonts and images in the CLONE
      if (document.fonts?.ready) { try { await document.fonts.ready; } catch { } }
      const imgs = Array.from(clone.querySelectorAll("img"))
        .filter(i => !i.complete || i.naturalWidth === 0)
        .map(i => new Promise(res => { i.onload = i.onerror = res; }));
      if (imgs.length) await Promise.all(imgs);

      // Two RAFs to ensure layout settles (avoids bottom cropping)
      await new Promise(r => requestAnimationFrame(r));
      await new Promise(r => requestAnimationFrame(r));

      // Measure the full content size
      const wPx = Math.ceil(clone.offsetWidth || DESKTOP_WIDTH);
      const hPx = Math.ceil(Math.max(clone.scrollHeight, clone.offsetHeight) + 1);

      // Render to canvas
      const H2C_SCALE = 2;
      const canvas = await html2canvas(clone, {
        scale: H2C_SCALE,
        useCORS: true,
        backgroundColor: "#ffffff",
        width: wPx,
        height: hPx,
        windowWidth: wPx,
        windowHeight: hPx + 100,
        scrollX: 0,
        scrollY: 0,
        removeContainer: true,
        logging: false,
      });

      // Remove the sandbox
      sandbox.remove();

      // Create a ONE-PAGE PDF sized exactly to the content
      const PX_TO_PT = 0.75; // 96dpi -> points
      const wPt = canvas.width * PX_TO_PT / H2C_SCALE;
      const hPt = canvas.height * PX_TO_PT / H2C_SCALE;

      const pdf = new jsPDF({
        unit: "pt",
        format: [wPt, hPt],
        orientation: wPt > hPt ? "landscape" : "portrait",
        compress: true,
      });

      pdf.addImage(canvas.toDataURL("image/jpeg", 0.98), "JPEG", 0, 0, wPt, hPt);
      const recipientEmail = (state?.email || state?.field3 || user_email || '').trim();
      const rawFirst = (state?.field1 || state?.firstName || state?.full_name || user_firstName || '').trim();
      const first = rawFirst ? rawFirst.split(' ')[0] : 'there';
      // Send
      const pdfBlob = pdf.output("blob");
      const formData = new FormData();
      formData.append("file", pdfBlob, "document.pdf");
      formData.append('to', recipientEmail);
      formData.append('cc', 'akim@cityrelay.com,dominique@cityrelay.com');
      formData.append("subject", "Your Property Rental Estimate from City Relay");
      const messageText = [
        `Hi ${first},`,
        '',
        'Please find attached your rental estimate prepared by City Relay.',
        'This estimate is based on current market data and our expertise in property management across London. It gives you a clear picture of the potential rental income for your property.',
        'If you’d like to discuss the details of this estimate or explore how City Relay can help maximize your property’s rental performance, feel free to book a call with our team:',
        'https://meetings.hubspot.com/akim-courail', 'https://meetings.hubspot.com/dominique-laretive', // ✅ keep as plain text
        '',
        'Best regards,',
        'The City Relay Team'
      ].join('\n');

      formData.append('message', messageText);
      formData.append('contentType', 'text/plain');

      //https://driveportal.cityrelaysolutions.com:443/public/sendValuationPdfFormViaEmail
      // https://drivestagingportal.cityrelaysolutions.com:443/public/sendValuationPdfFormViaEmail

      const res = await fetch("https://driveportal.cityrelaysolutions.com:443/public/sendValuationPdfFormViaEmail", {
        method: "POST",
        body: formData,
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      console.log("✅ PDF sent successfully");
    } catch (err) {
      console.error("❌ Error sending PDF:", err);
    }
  };
  const pdfFiredRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!sendToEmail || !submissionId) return;
    if (!chartReady) return;                       // ← NEW: wait for the chart

    const key = `pdf_sent_${submissionId}`;
    if (sessionStorage.getItem(key)) return;

    const fire = async () => {
      sessionStorage.setItem(key, 'in-progress');
      try {
        await generateAndSendPdf();
        sessionStorage.setItem(key, 'sent');
      } catch (e) {
        sessionStorage.removeItem(key);
        console.error(e);
      }
    };

    // Let the UI settle, then run
    requestAnimationFrame(() => setTimeout(fire, 0));
  }, [sendToEmail, submissionId, chartReady]);      // ← include chartReady here


  const section = wpPage?.pricingResult?.pricingResult
  let cityName = "london";//default value
  if (state && state.cityName) {
    cityName = state.cityName;//values can be: london , not_london
  }


  // todo test --------------------------------------------- start
  let occupancy = 0;//Avg. occupancy
  let annual_revenue = 0;//Annual revenue
  let occupancyRevenueByMonth = [];//array that maps Month=>Occupancy, Revenue
  let avg_daily_rate = 0;//Avg. daily rate
  let PerformanceChart;
  // let sendToEmail = true;  const submissionId = props.location?.state?.submissionId;
  const [barThickness, setBarThickness] = useState(30); // Default for desktop
  useEffect(() => {
    const updateBarThickness = () => {
      if (window.innerWidth <= 768) {
        setBarThickness(12); // Smaller thickness for mobile
      } else {
        setBarThickness(30); // Default for larger screens
      }
    };

    // Set on initial load
    updateBarThickness();

    // Add event listener for resize
    window.addEventListener('resize', updateBarThickness);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('resize', updateBarThickness);
    };
  }, []);
  // Track mobile viewport (<= 768px)
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    onResize(); // set once on mount
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (cityName == 'london') {
    if (state) {

      if (state.occupancy)
        occupancy = (state.occupancy * 100).toFixed(2);

      if (state.annual_revenue) {
        annual_revenue = state.annual_revenue.toFixed(2);
        // avg_daily_rate = (annual_revenue / 360).toFixed(2); // Returns a string with 2 decimal places
        // avg_daily_rate = ((annual_revenue * state.occupancy) / 360).toFixed(2);//as per Antanas-Martin
        // avg_daily_rate = ((annual_revenue * state.occupancy) / 360).toFixed(2);//as per Antanas-Martin
        avg_daily_rate = (annual_revenue / (365 * state.occupancy)).toFixed(2);
      }

      if (state.occupancyRevenueByMonth) {
        occupancyRevenueByMonth = state.occupancyRevenueByMonth;
      }
      // console.log('state===>', state);
      // console.log('occupancy=', occupancy);
      // console.log('annual_revenue=', annual_revenue);
      // console.log('occupancyRevenueByMonth=', occupancyRevenueByMonth);
    }

    // The API returns a rolling 12-month window (e.g. Sep 2026 - Aug 2027), but
    // the chart should always display Jan-Dec in calendar order, so index the
    // data by month number regardless of which year each month falls in.
    const monthAbbreviations = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    const byMonthIndex = {};
    occupancyRevenueByMonth
      .filter(item => item?.date)
      .forEach(item => {
        const monthIndex = Number(item.date.split('-')[1]) - 1;
        byMonthIndex[monthIndex] = item;
      });

    const labels = monthAbbreviations;

    const revenues = monthAbbreviations.map((_, i) => byMonthIndex[i]?.revenue ?? 0);

    const occupancies = monthAbbreviations.map((_, i) => (byMonthIndex[i]?.occupancy ?? 0) * 100);

    const chartReadyPlugin = {
      id: 'chartReady',
      afterRender: () => setChartReady(true),
    };

    // ===== CHART DATA (rename axes to avoid mixups) =====
    const chartData = {
      labels,
      datasets: [
        {
          type: 'bar',
          label: 'Average Revenue',
          data: revenues,
          backgroundColor: '#002244',
          borderRadius: 9,
          barThickness: barThickness,
          yAxisID: 'yRevenue',   // £ axis (left)
        },
        {
          type: 'line',
          label: 'Occupancy Rate',
          data: occupancies,
          borderColor: '#FFD700',
          borderWidth: 2,
          tension: 0.4,
          pointRadius: 3,
          pointHoverRadius: 4,
          yAxisID: 'yPercent',   // % axis (right)
        },
      ],
    };

    // ===== CHART OPTIONS =====
    const options = {
      responsive: true,
      // On mobile, fill the wrapper height (we set 320px). Desktop keeps aspect.
      maintainAspectRatio: !isMobile,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            usePointStyle: true,
            pointStyle: 'rect',
            font: { size: isMobile ? 12 : 18 },
          },
        },
        tooltip: {
          callbacks: {
            label: (ctx) =>
              ctx.dataset.label === 'Average Revenue'
                ? `£${Number(ctx.raw).toLocaleString('en-GB')}`
                : `${ctx.raw}%`,
          },
        },
      },
      animation: false, // ⛔ disable all animations
      // animation: {
      //   // If animations are enabled, this triggers when the first draw is done
      //   onComplete: () => setChartReady(true),
      // },

      layout: { padding: isMobile ? 4 : 16 },
      scales: {
        // Months bottom
        x: {
          ticks: {
            autoSkip: false,
            maxRotation: 0,
            font: { size: isMobile ? 11 : 14 },
          },
        },

        // £ axis — LEFT
        yRevenue: {
          type: 'linear',
          position: 'left',
          beginAtZero: true,
          ticks: {
            callback: (v) => `£${Number(v).toLocaleString('en-GB')}`,
            font: { size: isMobile ? 10 : 16 },
            display: true,
          },
          grid: { drawOnChartArea: true },
          title: { display: false },
        },

        // % axis — RIGHT
        yPercent: {
          type: 'linear',
          position: 'right',
          min: 0,
          max: 100,
          ticks: {
            stepSize: 10,
            callback: (v) => `${v}%`,
            font: { size: isMobile ? 10 : 16 },
            display: true,
            mirror: false,
          },
          grid: { drawOnChartArea: false }, // don’t duplicate grid
          title: { display: false },
        },
      },
    };

    PerformanceChart = () => (
      <Chart ref={chartRef} type="bar" data={chartData} options={options} plugins={[chartReadyPlugin]} />
    );


  }
  // todo test --------------------------------------------- end

  const handleScroll = () => {
    const element = document.getElementById('gatsby-focus-wrapper');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // if not_london => default = shiraz
  // if london => default = akim
  let widgetUrl = cityName == "not_london"
    ? "https://meetings.hubspot.com/livia-valeri-bressan?embed=true"
    : "https://meetings.hubspot.com/akim-courail?embed=true";

  const [isAgentSelected, setIsAgentSelected] = useState(false); // default is false
  const [selectedAgent, setSelectedAgent] = useState(null);

  const [user_address, setUserAddress] = useState(null);
  const [user_numOfBedrooms, setUserNumOfBedrooms] = useState(null);
  const [user_phone, setUserPhone] = useState(null);
  const [user_email, setUserEmail] = useState(null);
  const [user_firstName, setUserFirstName] = useState(null);
  const [user_lastName, setUserLastName] = useState(null);

  const onSelectAgent = (agent) => {
    setSelectedAgent(agent);
    setIsAgentSelected(true);
  };

  useEffect(() => {
  }, [selectedAgent]); // This runs whenever selectedAgent changes

  const [xValue, setXValue] = useState(0);
  const [yValue, setYValue] = useState(0);

  useEffect(() => {
    if (cityName == 'london') {
      if (state && state.property_address)
        setUserAddress(state.property_address)

      if (state && state.number_of_bedrooms)
        setUserNumOfBedrooms(state.number_of_bedrooms)

      if (state && state.field1)
        setUserFirstName(state.field1)

      if (state && state.field2)
        setUserLastName(state.field2)

      if (state && state.field3)
        setUserEmail(state.field3)

      if (state && state.field4)
        setUserPhone(state.field4)
    }

    if (typeof state?.estimate === 'undefined') {
      return navigate('/pricing/');
    }
    else {
      setXValue(Math.round(state.estimate * 1.1 / 10) * 10);
      setYValue(Math.round(state.estimate * 1.3 / 10) * 10);
    }
  }, []);

  const resultsLabels = {
    "en": {
      "heading": section?.heading,
      "income": "Potential income per month",
      "disclaimer": section?.disclaimer,
      "assumptionsHeading": section?.assumptions?.heading,
      "assumptions": section?.assumptions?.assumptions?.map((item) => (item.assumption)),
      "calendlyHeading": section?.calendlyHeading,
      "currency": "£",
    },
    ...pricingResultsLabels,
  }


  const new_labels = resultsLabels[state?.language || 'en'];

  // Do NOT move this code up
  if (selectedAgent) {
    if (selectedAgent == 'akim') {
      widgetUrl = "https://meetings.hubspot.com/akim-courail?embed=true";
    }
    else if (selectedAgent == 'dominique') {
      // widgetUrl = "https://meetings.hubspot.com/dominique-laretive?uuid=ad1b31f3-76ac-4828-af3d-3803a6dc911c";
      widgetUrl = "https://meetings.hubspot.com/dominique-laretive?embed=true";
    }
    else {
      widgetUrl = "https://meetings.hubspot.com/livia-valeri-bressan?embed=true";
    }
  }

  return state?.estimate ? (
    <Layout>
      <Seo post={wpPage} />
      <CustomSEO seo={wpPage?.seo} />
      <section className="my-20 md:my-36">

        {/* <button onClick={generateAndSendPdf} style={{ marginTop: 20 }}>
          Download Selected Parts as PDF11223
        </button> */}
        <div>
          {cityName === "london" ? (
            <div className="container" >
              <h1 className="text-h1 text-center mb-12">Your Property’s Estimated Revenue</h1>
              <h4 className='h4-after-h1'>This estimate reflects what you could earn from flexible letting in London.</h4>

              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 max-w-5xl mx-auto w-full">
                {/* LEFT COLUMN — User info */}
                <div className={`col-span-1 md:col-span-6 flex flex-col justify-center py-16 px-6 bg-navy-dark text-white`} id="img-form-cont">

                  <div id="center-icon">
                    <StaticImage src={'../../static/svg/key-hand.svg'} className="w-full lg:w-[42rem] " alt="CityRelay" />
                  </div>

                  <div id="form-data-container">
                    <div className='one-row'>
                      <label>Address:</label>
                      <span>{user_address}</span>
                    </div>

                    <div className='one-row'>
                      <label>Property:</label>
                      <span className="inline-flex items-center gap-2 align-middle">
                        {/* icon and bedroom count */}
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
                          xmlns="http://www.w3.org/2000/svg">
                          <path d="M3 7V17H5V13H21V17H23V11C23 9.34315 21.6569 8 20 8H11C9.34315 8 8 9.34315 8 11V12H5V7H3Z" fill="white" />
                          <circle cx="7" cy="10" r="2" fill="white" />
                        </svg>

                        &nbsp;{user_numOfBedrooms} {user_numOfBedrooms === 1 ? "bedroom" : "bedrooms"}
                      </span>
                    </div>

                    <div className='one-row'>
                      <label>Owner:</label>
                      <span>{user_firstName} {user_lastName}</span>
                    </div>

                    <div className='one-row'>
                      <label>Phone no:</label>
                      <span>{user_phone}</span>
                    </div>

                    <div className='one-row'>
                      <label>Email:</label>
                      <span>{user_email}</span>
                    </div>
                  </div>
                </div>

                {/* RIGHT COLUMN — Calendly (hide if sendToEmail is true) */}

                <div className="col-span-1 md:col-span-6 bg-white p-10 border border-opacity-5 border-black flex flex-col">
                  {section.calendlyHeading && <h3 className="text-h4 mb-8">{new_labels.calendlyHeading}</h3>}

                  {!isAgentSelected && cityName === "london" && (
                    <div className="custom1">
                      <label className="text-h5">Select your agent</label>

                      <div id="agents">
                        <div id="agent1" onClick={() => onSelectAgent('akim')}>
                          <img className="responsive-img" />
                          <span className='text-h3'>Akim Courail-Jones</span>
                        </div>
                        <div id="agent2" onClick={() => onSelectAgent('dominique')}>
                          <img className="responsive-img" />
                          <span className='text-h3'>Dominique Laretive</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {(isAgentSelected || cityName === "not_london") && (
                    <div className="bg-seafoam flex-auto p-5 flex items-center justify-center custom2">
                      <InlineWidget
                        styles={{ width: '100%' }}
                        pageSettings={{
                          hideEventTypeDetails: true,
                          hideLandingPageDetails: true,
                        }}
                        url={widgetUrl}
                        prefill={{
                          email: state?.email || '',
                          name: state?.full_name || '',
                        }}
                      />
                    </div>
                  )}
                </div>

              </div>
              <div id="yellow-cards-container">

                <div className={`w-48 lg:w-44 p-4 flex flex-col justify-center mb-2 bg-yellow`}>
                  <div className='top-left-icon'>
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="16" cy="16" r="14.5" stroke="#12203A" stroke-width="3" />
                      <path d="M8 14.6666L13.2929 19.9595C13.6834 20.35 14.3166 20.35 14.7071 19.9595L24 10.6666" stroke="#12203A" stroke-width="3" stroke-linecap="round" />
                    </svg>
                  </div>
                  <div className='beside-icon'>
                    <p className='text-h5 text-center'>Annual revenue</p>

                    <span>£{Number(annual_revenue).toLocaleString('en-GB', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}</span>
                  </div>
                </div>

                <div className={`w-48 lg:w-44 p-4 flex flex-col justify-center mb-2 bg-yellow`}>
                  <div className='top-left-icon'>
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="16" cy="16" r="14.5" stroke="#12203A" stroke-width="3" />
                      <path d="M8 14.6666L13.2929 19.9595C13.6834 20.35 14.3166 20.35 14.7071 19.9595L24 10.6666" stroke="#12203A" stroke-width="3" stroke-linecap="round" />
                    </svg>
                  </div>
                  <div className='beside-icon'>
                    <p className='text-h5 text-center'>Avg. daily rate</p>
                    <span>£{avg_daily_rate}</span>
                  </div>
                </div>

                <div className={`w-48 lg:w-44 p-4 flex flex-col justify-center mb-2 bg-yellow`}>
                  <div className='top-left-icon'>
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M26.3782 4.33901C20.6866 -1.40586 11.4154 -1.44905 5.67055 4.24264C-0.0743208 9.93432 -0.117508 19.2054 5.57418 24.9503C5.61855 24.995 5.66318 25.0395 5.70812 25.0837L10.9569 29.9304C13.7622 32.6766 18.2445 32.6884 21.0643 29.9571L26.3782 25.0464C32.0962 19.3282 32.0962 10.0573 26.3782 4.33901ZM24.0114 18.0206C24.0114 19.8586 22.5215 21.3485 20.6836 21.3485H11.3655C9.52755 21.3485 8.03762 19.8586 8.03762 18.0206V13.532C8.03762 12.2229 8.67918 10.9969 9.7548 10.2507L13.7482 7.48057C15.1181 6.53395 16.9309 6.53395 18.3008 7.48057L22.2942 10.2507C23.3699 10.9969 24.0114 12.2229 24.0114 13.532V18.0206ZM21.3491 13.5319V18.0206C21.3491 18.3881 21.0511 18.6861 20.6836 18.6861H18.6868V16.0238C18.6868 15.2886 18.0908 14.6927 17.3557 14.6927H14.6934C13.9582 14.6927 13.3622 15.2887 13.3622 16.0238V18.6861H11.3655C10.9979 18.6861 10.6999 18.3881 10.6999 18.0206V13.5319C10.6998 13.0954 10.9137 12.6866 11.2723 12.4378L15.2657 9.66764C15.7227 9.35339 16.3262 9.35339 16.7832 9.66764L20.7767 12.4378C21.1354 12.6866 21.3492 13.0954 21.3491 13.5319Z" fill="#12203A" />
                    </svg>
                  </div>
                  <div className='beside-icon'>
                    <p className='text-h5 text-center'>Avg. occupancy</p>
                    <span>{occupancy}%</span>
                  </div>
                </div>
              </div>

              <div className='max-w-5xl mx-auto w-full' id="chart-container">

                <div id="chart" className="cr-chart" style={isMobile ? { width: '100%', height: 320 } : undefined}
                >
                  <span>Performance indicators</span>
                  <PerformanceChart />
                </div>

                <div id="chart-p">
                  <p>Let us show you how City Relay can turn this projection into reality - where you get maximum rental income and occupancy without the hassle of managing it all yourself!
                  </p>
                </div>
              </div>

              {/* 2 sand boxes here */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 max-w-5xl mx-auto w-full">

                {/* first sand box */}
                <div id='get-started' className="col-span-1 md:col-span-6 flex flex-col justify-center py-16 px-6 bg-sand text-navy">
                  <h4>Get Started in 3 Simple Steps</h4>

                  {/* desktop only */}
                  <div className='one-row hidden lg:block'>
                    <svg width="72" height="71" viewBox="0 0 72 71" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="36" cy="35.5" r="35.5" fill="#F9DC72" />
                      <path d="M27.7129 32.6367V27.2227H28.5039C30.2148 27.1875 31.6152 26.8242 32.7051 26.1328C33.7949 25.4414 34.6152 24.3926 35.166 22.9863H40.3164V48H34.1289V30.9668C33.6133 31.4824 32.8926 31.8926 31.9668 32.1973C31.0527 32.4902 30.0566 32.6367 28.9785 32.6367H27.7129Z" fill="black" />
                    </svg>

                    <svg width="72" height="71" viewBox="0 0 72 71" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="36" cy="35.5" r="35.5" fill="#F9DC72" />
                      <path d="M24.0918 48V43.5703C29.3066 41.1328 33.209 38.9707 35.7988 37.084C37.1816 36.0762 38.1543 35.0977 38.7168 34.1484C39.291 33.1992 39.5781 32.1387 39.5781 30.9668C39.5781 29.748 39.209 28.7871 38.4707 28.084C37.7324 27.3691 36.6367 27.0117 35.1836 27.0117C33.7422 27.0117 32.6113 27.3867 31.791 28.1367C30.9824 28.875 30.5781 29.9062 30.5781 31.2305C30.5781 31.6523 30.6367 32.2031 30.7539 32.8828L24.3906 32.2676C24.2969 31.7871 24.25 31.2246 24.25 30.5801C24.25 28.084 25.2637 26.0801 27.291 24.5684C29.3301 23.0566 32.0254 22.3008 35.377 22.3008C38.7988 22.3008 41.459 23.0273 43.3574 24.4805C45.2559 25.9336 46.2051 27.9434 46.2051 30.5098C46.2051 32.2207 45.7832 33.7617 44.9395 35.1328C44.1074 36.4922 42.8711 37.7695 41.2305 38.9648C39.0039 40.5234 36.2676 41.9297 33.0215 43.1836H46.5918V48H24.0918Z" fill="black" />
                    </svg>

                    <svg width="72" height="71" viewBox="0 0 72 71" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="36" cy="35.5" r="35.5" fill="#F9DC72" />
                      <path d="M23.8105 40.2656L30.1211 39.6855C30.2148 41.0918 30.7129 42.1758 31.6152 42.9375C32.5293 43.6992 33.7832 44.0801 35.377 44.0801C36.9473 44.0801 38.1484 43.7812 38.9805 43.1836C39.8125 42.5742 40.2285 41.7188 40.2285 40.6172C40.2285 39.4688 39.8066 38.6367 38.9629 38.1211C38.1191 37.6055 36.8477 37.3477 35.1484 37.3477H33.1094V33.3398H35.1133C36.6133 33.3398 37.7559 33.0645 38.541 32.5137C39.3379 31.9629 39.7363 31.1602 39.7363 30.1055C39.7363 29.0625 39.3379 28.2598 38.541 27.6973C37.7559 27.123 36.625 26.8359 35.1484 26.8359C33.5898 26.8359 32.3887 27.2344 31.5449 28.0312C30.7012 28.8281 30.2617 29.9531 30.2266 31.4062L24.0391 30.6504C24.0742 28.1426 25.0938 26.127 27.0977 24.6035C29.1016 23.0684 31.8496 22.3008 35.3418 22.3008C38.7988 22.3008 41.459 22.9336 43.3223 24.1992C45.1973 25.4531 46.1348 27.2051 46.1348 29.4551C46.1348 30.9668 45.6133 32.2148 44.5703 33.1992C43.5391 34.1836 42.2031 34.8105 40.5625 35.0801C42.5547 35.3379 44.0898 35.9707 45.168 36.9785C46.2461 37.9746 46.7852 39.334 46.7852 41.0566C46.7852 43.3652 45.7715 45.2168 43.7441 46.6113C41.7168 47.9941 38.916 48.6855 35.3418 48.6855C31.8379 48.6855 29.0605 47.9355 27.0098 46.4355C24.9707 44.9238 23.9043 42.8672 23.8105 40.2656Z" fill="black" />
                    </svg>
                  </div>

                  <div className='one-row hidden lg:block'>
                    <div>
                      <div className='height-88px'><b>List your property across main booking platforms</b>  </div>

                      <p>From professional photography to listing optimisation, we’ll make your property listing stand out.
                      </p>
                    </div>

                    <div>
                      <div className='height-88px'>  <b>24/7 guest communication</b></div>

                      <p>Our capable support team is ready to handle guest inquiries, requests and check-ins, 24/7.
                      </p>
                    </div>

                    <div>
                      <div className='height-88px'><b>Real-time performance dashboard</b></div>

                      <p>Track bookings, income reports and even cleaning and maintenance schedules on our platform.
                      </p>
                    </div>
                  </div>

                  {/* mobile only */}
                  <div className='block lg:hidden'>
                    <svg width="72" height="71" viewBox="0 0 72 71" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="36" cy="35.5" r="35.5" fill="#F9DC72" />
                      <path d="M27.7129 32.6367V27.2227H28.5039C30.2148 27.1875 31.6152 26.8242 32.7051 26.1328C33.7949 25.4414 34.6152 24.3926 35.166 22.9863H40.3164V48H34.1289V30.9668C33.6133 31.4824 32.8926 31.8926 31.9668 32.1973C31.0527 32.4902 30.0566 32.6367 28.9785 32.6367H27.7129Z" fill="black" />
                    </svg>
                    <div>
                      <b>List your property across main booking platforms</b>
                      <p>From professional photography to listing optimisation, we’ll make your property listing stand out.
                      </p>
                    </div>
                  </div>

                  <div className='block lg:hidden'>
                    <svg width="72" height="71" viewBox="0 0 72 71" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="36" cy="35.5" r="35.5" fill="#F9DC72" />
                      <path d="M24.0918 48V43.5703C29.3066 41.1328 33.209 38.9707 35.7988 37.084C37.1816 36.0762 38.1543 35.0977 38.7168 34.1484C39.291 33.1992 39.5781 32.1387 39.5781 30.9668C39.5781 29.748 39.209 28.7871 38.4707 28.084C37.7324 27.3691 36.6367 27.0117 35.1836 27.0117C33.7422 27.0117 32.6113 27.3867 31.791 28.1367C30.9824 28.875 30.5781 29.9062 30.5781 31.2305C30.5781 31.6523 30.6367 32.2031 30.7539 32.8828L24.3906 32.2676C24.2969 31.7871 24.25 31.2246 24.25 30.5801C24.25 28.084 25.2637 26.0801 27.291 24.5684C29.3301 23.0566 32.0254 22.3008 35.377 22.3008C38.7988 22.3008 41.459 23.0273 43.3574 24.4805C45.2559 25.9336 46.2051 27.9434 46.2051 30.5098C46.2051 32.2207 45.7832 33.7617 44.9395 35.1328C44.1074 36.4922 42.8711 37.7695 41.2305 38.9648C39.0039 40.5234 36.2676 41.9297 33.0215 43.1836H46.5918V48H24.0918Z" fill="black" />
                    </svg>
                    <div>
                      <b>24/7 guest communication</b>
                      <p>Our capable support team is ready to handle guest inquiries, requests and check-ins, 24/7.
                      </p>
                    </div>
                  </div>
                  <div className='block lg:hidden'>
                    <svg width="72" height="71" viewBox="0 0 72 71" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="36" cy="35.5" r="35.5" fill="#F9DC72" />
                      <path d="M23.8105 40.2656L30.1211 39.6855C30.2148 41.0918 30.7129 42.1758 31.6152 42.9375C32.5293 43.6992 33.7832 44.0801 35.377 44.0801C36.9473 44.0801 38.1484 43.7812 38.9805 43.1836C39.8125 42.5742 40.2285 41.7188 40.2285 40.6172C40.2285 39.4688 39.8066 38.6367 38.9629 38.1211C38.1191 37.6055 36.8477 37.3477 35.1484 37.3477H33.1094V33.3398H35.1133C36.6133 33.3398 37.7559 33.0645 38.541 32.5137C39.3379 31.9629 39.7363 31.1602 39.7363 30.1055C39.7363 29.0625 39.3379 28.2598 38.541 27.6973C37.7559 27.123 36.625 26.8359 35.1484 26.8359C33.5898 26.8359 32.3887 27.2344 31.5449 28.0312C30.7012 28.8281 30.2617 29.9531 30.2266 31.4062L24.0391 30.6504C24.0742 28.1426 25.0938 26.127 27.0977 24.6035C29.1016 23.0684 31.8496 22.3008 35.3418 22.3008C38.7988 22.3008 41.459 22.9336 43.3223 24.1992C45.1973 25.4531 46.1348 27.2051 46.1348 29.4551C46.1348 30.9668 45.6133 32.2148 44.5703 33.1992C43.5391 34.1836 42.2031 34.8105 40.5625 35.0801C42.5547 35.3379 44.0898 35.9707 45.168 36.9785C46.2461 37.9746 46.7852 39.334 46.7852 41.0566C46.7852 43.3652 45.7715 45.2168 43.7441 46.6113C41.7168 47.9941 38.916 48.6855 35.3418 48.6855C31.8379 48.6855 29.0605 47.9355 27.0098 46.4355C24.9707 44.9238 23.9043 42.8672 23.8105 40.2656Z" fill="black" />
                    </svg>
                    <div>
                      <b>List your property across main booking platforms</b>
                      <p>From professional photography to listing optimisation, we’ll make your property listing stand out.
                      </p>
                    </div>
                  </div>


                </div>

                {/* second sand box */}
                <div id='choose-pricing' className="col-span-1 md:col-span-6 flex flex-col justify-center py-16 px-6 bg-sand text-navy">
                  <h4>Choose a Pricing Plan That Works for You!</h4>
                  <h5>Whether you’re hosting seasonally or all year round, we’ve got flexible plans that’ll suit your needs.</h5>
                  {/* desktop only */}
                  <div className='one-row hidden lg:block'>
                    <div id='btn-similar-1'>
                      <span >From 15% + VAT</span>
                      {/* <span>(of the Net booking)</span> */}
                    </div>
                    {/* 
                  <div id='btn-similar-2'>
                    <span>From 13% + VAT</span>
                    <span>(of the Net booking)</span>
                  </div> */}
                  </div>

                  <div className='one-row hidden lg:block'>
                    <span>For short-term hosts who want the flexibility to use their property whenever they like.
                    </span>
                    {/* <span>For full-time hosts who can keep their properties available all year.
                  </span> */}
                  </div>

                  {/* mobile only */}
                  <div className='block lg:hidden'>
                    <div id='btn-similar-1'>
                      <span >From 15% + VAT</span>
                      {/* <span>(of the Net booking)</span> */}
                    </div>
                    <span className='span'>For short-term hosts who want the flexibility to use their property whenever they like.
                    </span>
                  </div>

                  {/* <div className='block lg:hidden'>
                  <div id='btn-similar-2'>
                    <span>From 13% + VAT</span>
                    <span>(of the Net booking)</span>
                  </div>
                  <span className='span'>For full-time hosts who can keep their properties available all year.
                  </span>
                </div> */}

                </div>
              </div>

              {/* comparison + FAQ */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 max-w-5xl mx-auto w-full" id="comp-faq">

                {/* comparison box */}
                <div className="col-span-1 md:col-span-8 flex flex-col">
                  <div id='comparison-container' className='bg-navy text-white'>
                    <label id="comparison-title">Why Partner with City Relay</label>

                    <table className="comparison-table">
                      <thead>
                        <tr>
                          <th id='empty-th'></th>
                          <th className="highlighted-column" id='managed-by-start'>Managed by Cityrelay</th>
                          <th>Hosting Yourself</th>
                          <th>Estate Agent</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((feature, idx) => (
                          <tr key={idx} className={idx % 2 === 0 ? 'semi-trans-bg' : ''}>
                            <td className="col-a">{feature}</td>

                            {/* Managed by CityRelay: always a dark check */}
                            <td className="highlighted-column col-b">
                              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <img src={CHECK_DARK_SVG} width={24} height={24} alt="" />
                              </div>
                            </td>

                            {/* Hosting Yourself: check for specific rows, otherwise a cross */}
                            <td className="col-c">
                              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                {(idx === 0 || idx === 1 || idx === 4 || idx === 6 || idx === 8 || idx === 9) ? (
                                  <img src={CHECK_LIGHT_SVG} width={24} height={24} alt="" />
                                ) : (
                                  <img src={CROSS_SVG} width={24} height={24} alt="" />
                                )}
                              </div>
                            </td>

                            {/* Estate Agent: check for rows 2, 4, 9; otherwise a cross */}
                            <td className="col-d">
                              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                {(idx === 2 || idx === 4 || idx === 9) ? (
                                  <img src={CHECK_LIGHT_SVG} width={24} height={24} alt="" />
                                ) : (
                                  <img src={CROSS_SVG} width={24} height={24} alt="" />
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                  </div>

                  {/* text below the table */}
                  {/* <div id='after-table'>
                  <label>City Relay is ready to manage your property and optimise its potential</label>
                  <button onClick={handleScroll} className='btn btn--primary false !py-4'>Book a Call Now!</button>
                  {sendToEmail && (
                    <div className="flex items-center gap-6 flex-wrap text-navy-dark font-bold text-lg">
                
                      <div className="flex items-center gap-2">
                        <div className="bg-navy-dark p-2 rounded">
                          <svg xmlns="http://www.w3.org/2000/svg" className="text-white w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M6.62 10.79a15.053 15.053 0 006.59 6.59l2.2-2.2a1.003 1.003 0 011.11-.21 11.72 11.72 0 003.69.59 1 1 0 011 1v3.5a1 1 0 01-1 1A17.92 17.92 0 014 6a1 1 0 011-1h3.5a1 1 0 011 1c0 1.27.21 2.52.59 3.69a1.003 1.003 0 01-.21 1.11l-2.26 2.26z" />
                          </svg>
                        </div>
                        <span>+44 20 3865 0599</span>
                      </div>

                
                      <div className="flex items-center gap-2">
                        <div className="bg-navy-dark p-2 rounded">
                          <svg xmlns="http://www.w3.org/2000/svg" className="text-white w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M20 4H4a2 2 0 00-2 2v1.2l10 6.2 10-6.2V6a2 2 0 00-2-2zm0 4.8l-8.4 5.2a1 1 0 01-1.2 0L4 8.8V18a2 2 0 002 2h12a2 2 0 002-2V8.8z" />
                          </svg>
                        </div>
                        <span>info@cityrelay.com</span>
                      </div>
                    </div>
                  )}
                </div> */}

                </div>

                {/* FAQ box */}
                <div id='faq' className="col-span-1 md:col-span-4 flex flex-col px-6 bg-sand text-navy">
                  <label id="faq-title">Frequently Asked Questions</label>

                  {/* <div className="faq-details">
                  <span className='que'>Why does City Relay charge a commission on the net rent instead of the gross rent?</span>
                  <span className='ans'>At City Relay, we believe in fair and transparent pricing. Unlike competitors who charge a commission on the gross rent (the total booking amount), we charge our commission on the net rent.
                    <br />
                    Net rent is the amount left after deducting channel fees (like Airbnb or Booking.com fees) and payment processing fees. By charging a commission only on the net rent, we ensure you’re paying based on what remains in your pocket after all fees. This approach is more aligned with your actual earnings and avoids the inflated charges that come with gross rent-based commissions.
                  </span>
                </div> */}

                  <div className="faq-details">
                    <span className='que'>What will the call be about?
                    </span>
                    <span className='ans'>We’ll discuss your property’s unique features to refine the revenue estimate and get a more accurate projection. We’ll also answer your questions about getting started so we can list your property immediately.</span>
                  </div>

                  <div className="faq-details">
                    <span className='que'>How will City Relay help me earn more?</span>
                    <span className='ans'>We use flexible letting and a smart pricing algorithm that automatically adjusts rates to ensure maximum earning potential. This dynamic pricing strategy will keep your property competitive in London’s private rental market.
                    </span>
                  </div>

                  <div className="faq-details">
                    <span className='que'>Does City Relay handle everything?</span>
                    <span className='ans'>Yes! We’ll take care of the whole property management process, from professional cleaning and hotel-quality linens to 24/7 guest support and maintenance. We’ll even list your property if you haven’t done it yet.
                    </span>
                  </div>

                </div>

              </div>
              {/* text below the table */}
              <div id='after-table'>
                <label>City Relay is ready to manage your property and optimise its potential</label>
                <div className="inline-flex gap-8">
                  <button onClick={handleScroll} className='btn btn--primary false !py-4'>Book a Call Now!</button>

                </div>
              </div>
            </div>
          ) : (
            <div className="container">
              <h1 className="text-h1 text-center mb-12">{new_labels.heading}</h1>

              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 max-w-5xl mx-auto w-full">
                <div className="col-span-1 md:col-span-5 flex flex-col justify-center py-16 px-6 bg-navy-dark text-white">
                  {state?.estimate && (
                    <div className="space-y-3 mb-12 md:mb-16 text-center max-w-xs mx-auto">
                      <h2 className="text-h6">{new_labels.income}</h2>
                      <h3 className="text-h2">{new_labels.currency}{xValue} - {new_labels.currency}{yValue}</h3>
                      <p className="opacity-40 text-xs">{new_labels.disclaimer}</p>
                    </div>
                  )}

                  {section.assumptions && (
                    <div className="max-w-xs mx-auto">
                      <h4 className="text-h5 mb-6 text-center">{new_labels.assumptionsHeading}</h4>
                      <div className="flex flex-col justify-center">
                        <div className="text-sm space-y-3 max-w-[16rem]  mx-auto">
                          {section.assumptions.assumptions?.map((item, i) => (
                            <div key={`assumption${i}`} className="grid grid-cols-12 gap-4 items-center">
                              <div className="col-span-3">
                                <div className="aspect-w-1 aspect-h-1">
                                  <div>{item.illustration && <Image data={item.illustration} />}</div>
                                </div>
                              </div>
                              <div className="col-span-9">{new_labels.assumptions[i]}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                </div>

                <div className="col-span-1 md:col-span-7 bg-white p-10 border border-opacity-5 border-black flex flex-col">
                  {section.calendlyHeading && <h3 className="text-h4 mb-8">{new_labels.calendlyHeading}</h3>}

                  {/* if the agent is already selected OR city is not_london */}

                  {(isAgentSelected || cityName == "not_london") && <div className="bg-seafoam flex-auto p-5 flex items-center justify-center custom2">
                    <InlineWidget
                      styles={{
                        width: '100%',
                      }}
                      pageSettings={{
                        hideEventTypeDetails: true,
                        hideLandingPageDetails: true,
                      }}
                      url={widgetUrl}
                      prefill={{
                        email: state?.email || '',
                        name: state?.full_name || '',
                      }}
                    />
                  </div>}

                </div>
              </div>

            </div>
          )}
        </div>
        <div id="pdfContent" >
          <div style={{ height: '160px', alignItems: 'right', width: '100%' }}>
            <img src='https://cityrelay.com/cityrelay-logo.jpg ' alt="CityRelay" style={{ height: '160px', width: '300px', float: 'right' }} />
          </div>
          {cityName === "london" ? (
            <div className="container" >
              <h1 className="text-h1 text-center mb-12">Your Property’s Estimated Revenue </h1>
              <h4 className='h4-after-h1'>This estimate reflects what you could earn from flexible letting in London.</h4>

              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 max-w-5xl mx-auto w-full">
                {/* LEFT COLUMN — User info */}
                <div className={`col-span-1 ${sendToEmail ? 'md:col-span-12 mx-auto' : 'md:col-span-6'} flex flex-col justify-center py-16 px-6 bg-navy-dark text-white`} id="img-form-cont">

                  <div id="center-icon">
                    <StaticImage src={'../../static/svg/key-hand.svg'} className="w-full lg:w-[42rem] " alt="CityRelay" />
                  </div>

                  <div id="form-data-container">
                    <div className='one-row'>
                      <label>Address:</label>
                      <span>{user_address}</span>
                    </div>

                    <div className='one-row'>
                      <label>Property:</label>
                      <span >
                        {/* icon and bedroom count */}
                        <svg className="relative top-[5px]" width="24" height="24" viewBox="0 0 24 24" fill="none"
                          xmlns="http://www.w3.org/2000/svg">
                          <path d="M3 7V17H5V13H21V17H23V11C23 9.34315 21.6569 8 20 8H11C9.34315 8 8 9.34315 8 11V12H5V7H3Z" fill="white" />
                          <circle cx="7" cy="10" r="2" fill="white" />
                        </svg>

                        &nbsp;{user_numOfBedrooms} {user_numOfBedrooms === 1 ? "bedroom" : "bedrooms"}
                      </span>
                    </div>

                    <div className='one-row'>
                      <label>Owner:</label>
                      <span>{user_firstName} {user_lastName}</span>
                    </div>

                    <div className='one-row'>
                      <label>Phone no:</label>
                      <span>{user_phone}</span>
                    </div>

                    <div className='one-row'>
                      <label>Email:</label>
                      <span>{user_email}</span>
                    </div>
                  </div>
                </div>

                {/* RIGHT COLUMN — Calendly (hide if sendToEmail is true) */}
                {!sendToEmail && (
                  <div className="col-span-1 md:col-span-6 bg-white p-10 border border-opacity-5 border-black flex flex-col">
                    {section.calendlyHeading && <h3 className="text-h4 mb-8">{new_labels.calendlyHeading}</h3>}

                    {!isAgentSelected && cityName === "london" && (
                      <div className="custom1">
                        <label className="text-h5">Select your agent</label>

                        <div id="agents">
                          <div id="agent1" onClick={() => onSelectAgent('akim')}>
                            <img className="responsive-img" />
                            <span className='text-h3'>Akim Courail-Jones</span>
                          </div>
                          <div id="agent2" onClick={() => onSelectAgent('dominique')}>
                            <img className="responsive-img" />
                            <span className='text-h3'>Dominique Laretive</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {(isAgentSelected || cityName === "not_london") && (
                      <div className="bg-seafoam flex-auto p-5 flex items-center justify-center custom2">
                        <InlineWidget
                          styles={{ width: '100%' }}
                          pageSettings={{
                            hideEventTypeDetails: true,
                            hideLandingPageDetails: true,
                          }}
                          url={widgetUrl}
                          prefill={{
                            email: state?.email || '',
                            name: state?.full_name || '',
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div id="yellow-cards-container">

                <div className={`w-48 lg:w-44 p-4 flex flex-col justify-center mb-2 bg-yellow`}>
                  <div className='top-left-icon'>
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="16" cy="16" r="14.5" stroke="#12203A" stroke-width="3" />
                      <path d="M8 14.6666L13.2929 19.9595C13.6834 20.35 14.3166 20.35 14.7071 19.9595L24 10.6666" stroke="#12203A" stroke-width="3" stroke-linecap="round" />
                    </svg>
                  </div>
                  <div className='beside-icon'>
                    <p className='text-h5 text-center'>Annual revenue</p>
                    <span>£{Number(annual_revenue).toLocaleString('en-GB', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}</span>
                  </div>
                </div>

                <div className={`w-48 lg:w-44 p-4 flex flex-col justify-center mb-2 bg-yellow`}>
                  <div className='top-left-icon'>
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="16" cy="16" r="14.5" stroke="#12203A" stroke-width="3" />
                      <path d="M8 14.6666L13.2929 19.9595C13.6834 20.35 14.3166 20.35 14.7071 19.9595L24 10.6666" stroke="#12203A" stroke-width="3" stroke-linecap="round" />
                    </svg>
                  </div>
                  <div className='beside-icon'>
                    <p className='text-h5 text-center'>Avg. daily rate</p>
                    <span>£{avg_daily_rate}</span>
                  </div>
                </div>

                <div className={`w-48 lg:w-44 p-4 flex flex-col justify-center mb-2 bg-yellow`}>
                  <div className='top-left-icon'>
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M26.3782 4.33901C20.6866 -1.40586 11.4154 -1.44905 5.67055 4.24264C-0.0743208 9.93432 -0.117508 19.2054 5.57418 24.9503C5.61855 24.995 5.66318 25.0395 5.70812 25.0837L10.9569 29.9304C13.7622 32.6766 18.2445 32.6884 21.0643 29.9571L26.3782 25.0464C32.0962 19.3282 32.0962 10.0573 26.3782 4.33901ZM24.0114 18.0206C24.0114 19.8586 22.5215 21.3485 20.6836 21.3485H11.3655C9.52755 21.3485 8.03762 19.8586 8.03762 18.0206V13.532C8.03762 12.2229 8.67918 10.9969 9.7548 10.2507L13.7482 7.48057C15.1181 6.53395 16.9309 6.53395 18.3008 7.48057L22.2942 10.2507C23.3699 10.9969 24.0114 12.2229 24.0114 13.532V18.0206ZM21.3491 13.5319V18.0206C21.3491 18.3881 21.0511 18.6861 20.6836 18.6861H18.6868V16.0238C18.6868 15.2886 18.0908 14.6927 17.3557 14.6927H14.6934C13.9582 14.6927 13.3622 15.2887 13.3622 16.0238V18.6861H11.3655C10.9979 18.6861 10.6999 18.3881 10.6999 18.0206V13.5319C10.6998 13.0954 10.9137 12.6866 11.2723 12.4378L15.2657 9.66764C15.7227 9.35339 16.3262 9.35339 16.7832 9.66764L20.7767 12.4378C21.1354 12.6866 21.3492 13.0954 21.3491 13.5319Z" fill="#12203A" />
                    </svg>
                  </div>
                  <div className='beside-icon'>
                    <p className='text-h5 text-center'>Avg. occupancy</p>
                    <span>{occupancy}%</span>
                  </div>
                </div>
              </div>

              <div className='max-w-5xl mx-auto w-full' id="chart-container">

                <div id="chart" className="cr-chart">
                  <span>Performance indicators</span>
                  <PerformanceChart />
                </div>

                <div id="chart-p">
                  <p>Let us show you how City Relay can turn this projection into reality - where you get maximum rental income and occupancy without the hassle of managing it all yourself!
                  </p>
                </div>
              </div>

              {/* 2 sand boxes here */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 max-w-5xl mx-auto w-full">

                {/* first sand box */}
                <div id='get-started' className="col-span-1 md:col-span-6 flex flex-col justify-center py-16 px-6 bg-sand text-navy">
                  <h4>Get Started in 3 Simple Steps</h4>

                  {/* desktop only */}
                  <div className='one-row hidden lg:block'>
                    <svg width="72" height="71" viewBox="0 0 72 71" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="36" cy="35.5" r="35.5" fill="#F9DC72" />
                      <path d="M27.7129 32.6367V27.2227H28.5039C30.2148 27.1875 31.6152 26.8242 32.7051 26.1328C33.7949 25.4414 34.6152 24.3926 35.166 22.9863H40.3164V48H34.1289V30.9668C33.6133 31.4824 32.8926 31.8926 31.9668 32.1973C31.0527 32.4902 30.0566 32.6367 28.9785 32.6367H27.7129Z" fill="black" />
                    </svg>

                    <svg width="72" height="71" viewBox="0 0 72 71" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="36" cy="35.5" r="35.5" fill="#F9DC72" />
                      <path d="M24.0918 48V43.5703C29.3066 41.1328 33.209 38.9707 35.7988 37.084C37.1816 36.0762 38.1543 35.0977 38.7168 34.1484C39.291 33.1992 39.5781 32.1387 39.5781 30.9668C39.5781 29.748 39.209 28.7871 38.4707 28.084C37.7324 27.3691 36.6367 27.0117 35.1836 27.0117C33.7422 27.0117 32.6113 27.3867 31.791 28.1367C30.9824 28.875 30.5781 29.9062 30.5781 31.2305C30.5781 31.6523 30.6367 32.2031 30.7539 32.8828L24.3906 32.2676C24.2969 31.7871 24.25 31.2246 24.25 30.5801C24.25 28.084 25.2637 26.0801 27.291 24.5684C29.3301 23.0566 32.0254 22.3008 35.377 22.3008C38.7988 22.3008 41.459 23.0273 43.3574 24.4805C45.2559 25.9336 46.2051 27.9434 46.2051 30.5098C46.2051 32.2207 45.7832 33.7617 44.9395 35.1328C44.1074 36.4922 42.8711 37.7695 41.2305 38.9648C39.0039 40.5234 36.2676 41.9297 33.0215 43.1836H46.5918V48H24.0918Z" fill="black" />
                    </svg>

                    <svg width="72" height="71" viewBox="0 0 72 71" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="36" cy="35.5" r="35.5" fill="#F9DC72" />
                      <path d="M23.8105 40.2656L30.1211 39.6855C30.2148 41.0918 30.7129 42.1758 31.6152 42.9375C32.5293 43.6992 33.7832 44.0801 35.377 44.0801C36.9473 44.0801 38.1484 43.7812 38.9805 43.1836C39.8125 42.5742 40.2285 41.7188 40.2285 40.6172C40.2285 39.4688 39.8066 38.6367 38.9629 38.1211C38.1191 37.6055 36.8477 37.3477 35.1484 37.3477H33.1094V33.3398H35.1133C36.6133 33.3398 37.7559 33.0645 38.541 32.5137C39.3379 31.9629 39.7363 31.1602 39.7363 30.1055C39.7363 29.0625 39.3379 28.2598 38.541 27.6973C37.7559 27.123 36.625 26.8359 35.1484 26.8359C33.5898 26.8359 32.3887 27.2344 31.5449 28.0312C30.7012 28.8281 30.2617 29.9531 30.2266 31.4062L24.0391 30.6504C24.0742 28.1426 25.0938 26.127 27.0977 24.6035C29.1016 23.0684 31.8496 22.3008 35.3418 22.3008C38.7988 22.3008 41.459 22.9336 43.3223 24.1992C45.1973 25.4531 46.1348 27.2051 46.1348 29.4551C46.1348 30.9668 45.6133 32.2148 44.5703 33.1992C43.5391 34.1836 42.2031 34.8105 40.5625 35.0801C42.5547 35.3379 44.0898 35.9707 45.168 36.9785C46.2461 37.9746 46.7852 39.334 46.7852 41.0566C46.7852 43.3652 45.7715 45.2168 43.7441 46.6113C41.7168 47.9941 38.916 48.6855 35.3418 48.6855C31.8379 48.6855 29.0605 47.9355 27.0098 46.4355C24.9707 44.9238 23.9043 42.8672 23.8105 40.2656Z" fill="black" />
                    </svg>
                  </div>

                  <div className='one-row hidden lg:block'>
                    <div>
                      <div className='height-88px'>
                        <b>List your property across main booking platforms</b>
                      </div>

                      <p>From professional photography to listing optimisation, we’ll make your property listing stand out.
                      </p>
                    </div>

                    <div>
                      <div className='height-88px'>
                        <b>24/7 guest communication</b>
                      </div>

                      <p>Our capable support team is ready to handle guest inquiries, requests and check-ins, 24/7.
                      </p>
                    </div>

                    <div>
                      <div className='height-88px'>
                        <b>Real-time performance dashboard</b>
                      </div>
                      <p>Track bookings, income reports and even cleaning and maintenance schedules on our platform.
                      </p>
                    </div>
                  </div>

                  {/* mobile only */}
                  <div className='block lg:hidden'>
                    <svg width="72" height="71" viewBox="0 0 72 71" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="36" cy="35.5" r="35.5" fill="#F9DC72" />
                      <path d="M27.7129 32.6367V27.2227H28.5039C30.2148 27.1875 31.6152 26.8242 32.7051 26.1328C33.7949 25.4414 34.6152 24.3926 35.166 22.9863H40.3164V48H34.1289V30.9668C33.6133 31.4824 32.8926 31.8926 31.9668 32.1973C31.0527 32.4902 30.0566 32.6367 28.9785 32.6367H27.7129Z" fill="black" />
                    </svg>
                    <div>
                      <b>List your property across main booking platforms</b>
                      <p>From professional photography to listing optimisation, we’ll make your property listing stand out.
                      </p>
                    </div>
                  </div>

                  <div className='block lg:hidden'>
                    <svg width="72" height="71" viewBox="0 0 72 71" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="36" cy="35.5" r="35.5" fill="#F9DC72" />
                      <path d="M24.0918 48V43.5703C29.3066 41.1328 33.209 38.9707 35.7988 37.084C37.1816 36.0762 38.1543 35.0977 38.7168 34.1484C39.291 33.1992 39.5781 32.1387 39.5781 30.9668C39.5781 29.748 39.209 28.7871 38.4707 28.084C37.7324 27.3691 36.6367 27.0117 35.1836 27.0117C33.7422 27.0117 32.6113 27.3867 31.791 28.1367C30.9824 28.875 30.5781 29.9062 30.5781 31.2305C30.5781 31.6523 30.6367 32.2031 30.7539 32.8828L24.3906 32.2676C24.2969 31.7871 24.25 31.2246 24.25 30.5801C24.25 28.084 25.2637 26.0801 27.291 24.5684C29.3301 23.0566 32.0254 22.3008 35.377 22.3008C38.7988 22.3008 41.459 23.0273 43.3574 24.4805C45.2559 25.9336 46.2051 27.9434 46.2051 30.5098C46.2051 32.2207 45.7832 33.7617 44.9395 35.1328C44.1074 36.4922 42.8711 37.7695 41.2305 38.9648C39.0039 40.5234 36.2676 41.9297 33.0215 43.1836H46.5918V48H24.0918Z" fill="black" />
                    </svg>
                    <div >
                      <b>24/7 guest communication</b>
                      <p>Our capable support team is ready to handle guest inquiries, requests and check-ins, 24/7.
                      </p>
                    </div>
                  </div>
                  <div className='block lg:hidden'>
                    <svg width="72" height="71" viewBox="0 0 72 71" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="36" cy="35.5" r="35.5" fill="#F9DC72" />
                      <path d="M23.8105 40.2656L30.1211 39.6855C30.2148 41.0918 30.7129 42.1758 31.6152 42.9375C32.5293 43.6992 33.7832 44.0801 35.377 44.0801C36.9473 44.0801 38.1484 43.7812 38.9805 43.1836C39.8125 42.5742 40.2285 41.7188 40.2285 40.6172C40.2285 39.4688 39.8066 38.6367 38.9629 38.1211C38.1191 37.6055 36.8477 37.3477 35.1484 37.3477H33.1094V33.3398H35.1133C36.6133 33.3398 37.7559 33.0645 38.541 32.5137C39.3379 31.9629 39.7363 31.1602 39.7363 30.1055C39.7363 29.0625 39.3379 28.2598 38.541 27.6973C37.7559 27.123 36.625 26.8359 35.1484 26.8359C33.5898 26.8359 32.3887 27.2344 31.5449 28.0312C30.7012 28.8281 30.2617 29.9531 30.2266 31.4062L24.0391 30.6504C24.0742 28.1426 25.0938 26.127 27.0977 24.6035C29.1016 23.0684 31.8496 22.3008 35.3418 22.3008C38.7988 22.3008 41.459 22.9336 43.3223 24.1992C45.1973 25.4531 46.1348 27.2051 46.1348 29.4551C46.1348 30.9668 45.6133 32.2148 44.5703 33.1992C43.5391 34.1836 42.2031 34.8105 40.5625 35.0801C42.5547 35.3379 44.0898 35.9707 45.168 36.9785C46.2461 37.9746 46.7852 39.334 46.7852 41.0566C46.7852 43.3652 45.7715 45.2168 43.7441 46.6113C41.7168 47.9941 38.916 48.6855 35.3418 48.6855C31.8379 48.6855 29.0605 47.9355 27.0098 46.4355C24.9707 44.9238 23.9043 42.8672 23.8105 40.2656Z" fill="black" />
                    </svg>
                    <div>
                      <b>List your property across main booking platforms</b>
                      <p>From professional photography to listing optimisation, we’ll make your property listing stand out.
                      </p>
                    </div>
                  </div>


                </div>

                {/* second sand box */}
                <div id='choose-pricing' className="col-span-1 md:col-span-6 flex flex-col justify-center py-16 px-6 bg-sand text-navy">
                  <h4>Choose a Pricing Plan That Works for You!</h4>
                  <h5>Whether you’re hosting seasonally or all year round, we’ve got flexible plans that’ll suit your needs.</h5>
                  {/* desktop only */}
                  <div className="one-row hidden lg:block">
                    <div
                      id="btn-similar-1"
                      className="inline-flex items-center justify-center h-12 px-6 rounded-full bg-yellow text-navy-dark font-bold leading-none mx-auto whitespace-nowrap" style={{ height: '48px' }}
                    >

                      <span style={{ height: '25px', marginTop: '-30px', backgroundColor: 'transparent' }}>From 15% + VAT</span>
                      {/* <span>(of the Net booking)</span> */}
                    </div>

                    {/*
                    <div id="btn-similar-2">
                      <span>From 13% + VAT</span>
                      <span>(of the Net booking)</span>
                    </div>
                    */}
                  </div>
                  <div className='one-row hidden lg:block'>
                    <span>For short-term hosts who want the flexibility to use their property whenever they like.
                    </span>
                    {/* <span>For full-time hosts who can keep their properties available all year.
                  </span> */}
                  </div>

                  {/* mobile only */}
                  <div className='block lg:hidden'>
                    <div id='btn-similar-1'>
                      <span >From 15% + VAT</span>
                      {/* <span>(of the Net booking)</span> */}
                    </div>
                    <span className='span'>For short-term hosts who want the flexibility to use their property whenever they like.
                    </span>
                  </div>

                  {/* <div className='block lg:hidden'>
                  <div id='btn-similar-2'>
                    <span>From 13% + VAT</span>
                    <span>(of the Net booking)</span>
                  </div>
                  <span className='span'>For full-time hosts who can keep their properties available all year.
                  </span>
                </div> */}

                </div>
              </div>

              {/* comparison + FAQ */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 max-w-5xl mx-auto w-full" id="comp-faq">

                {/* comparison box */}
                <div className="col-span-1 md:col-span-8 flex flex-col">
                  <div id='comparison-container' className='bg-navy text-white'>
                    <label id="comparison-title">Why Partner with City Relay</label>

                    <table className="comparison-table">
                      <thead>
                        <tr>
                          <th id='empty-th'></th>
                          <th className="highlighted-column" id='managed-by-start'>Managed by Cityrelay</th>
                          <th>Hosting Yourself</th>
                          <th>Estate Agent</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((feature, idx) => (
                          <tr key={idx} className={idx % 2 === 0 ? 'semi-trans-bg' : ''}>
                            <td className="col-a">{feature}</td>

                            {/* Managed by CityRelay: always a dark check */}
                            <td className="highlighted-column col-b">
                              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <img src={CHECK_DARK_SVG} width={24} height={24} alt="" />
                              </div>
                            </td>

                            {/* Hosting Yourself: check for specific rows, otherwise a cross */}
                            <td className="col-c">
                              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                {(idx === 0 || idx === 1 || idx === 4 || idx === 6 || idx === 8 || idx === 9) ? (
                                  <img src={CHECK_LIGHT_SVG} width={24} height={24} alt="" />
                                ) : (
                                  <img src={CROSS_SVG} width={24} height={24} alt="" />
                                )}
                              </div>
                            </td>

                            {/* Estate Agent: check for rows 2, 4, 9; otherwise a cross */}
                            <td className="col-d">
                              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                {(idx === 2 || idx === 4 || idx === 9) ? (
                                  <img src={CHECK_LIGHT_SVG} width={24} height={24} alt="" />
                                ) : (
                                  <img src={CROSS_SVG} width={24} height={24} alt="" />
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                  </div>

                  {/* text below the table */}
                  {/* <div id='after-table'>
                  <label>City Relay is ready to manage your property and optimise its potential</label>
                  <button onClick={handleScroll} className='btn btn--primary false !py-4'>Book a Call Now!</button>
                  {sendToEmail && (
                    <div className="flex items-center gap-6 flex-wrap text-navy-dark font-bold text-lg">
                
                      <div className="flex items-center gap-2">
                        <div className="bg-navy-dark p-2 rounded">
                          <svg xmlns="http://www.w3.org/2000/svg" className="text-white w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M6.62 10.79a15.053 15.053 0 006.59 6.59l2.2-2.2a1.003 1.003 0 011.11-.21 11.72 11.72 0 003.69.59 1 1 0 011 1v3.5a1 1 0 01-1 1A17.92 17.92 0 014 6a1 1 0 011-1h3.5a1 1 0 011 1c0 1.27.21 2.52.59 3.69a1.003 1.003 0 01-.21 1.11l-2.26 2.26z" />
                          </svg>
                        </div>
                        <span>+44 20 3865 0599</span>
                      </div>

                
                      <div className="flex items-center gap-2">
                        <div className="bg-navy-dark p-2 rounded">
                          <svg xmlns="http://www.w3.org/2000/svg" className="text-white w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M20 4H4a2 2 0 00-2 2v1.2l10 6.2 10-6.2V6a2 2 0 00-2-2zm0 4.8l-8.4 5.2a1 1 0 01-1.2 0L4 8.8V18a2 2 0 002 2h12a2 2 0 002-2V8.8z" />
                          </svg>
                        </div>
                        <span>info@cityrelay.com</span>
                      </div>
                    </div>
                  )}
                </div> */}

                </div>

                {/* FAQ box */}
                <div id='faq' className="col-span-1 md:col-span-4 flex flex-col px-6 bg-sand text-navy">
                  <label id="faq-title">Frequently Asked Questions</label>

                  {/* <div className="faq-details">
                  <span className='que'>Why does City Relay charge a commission on the net rent instead of the gross rent?</span>
                  <span className='ans'>At City Relay, we believe in fair and transparent pricing. Unlike competitors who charge a commission on the gross rent (the total booking amount), we charge our commission on the net rent.
                    <br />
                    Net rent is the amount left after deducting channel fees (like Airbnb or Booking.com fees) and payment processing fees. By charging a commission only on the net rent, we ensure you’re paying based on what remains in your pocket after all fees. This approach is more aligned with your actual earnings and avoids the inflated charges that come with gross rent-based commissions.
                  </span>
                </div> */}

                  <div className="faq-details">
                    <span className='que'>What will the call be about?
                    </span>
                    <span className='ans'>We’ll discuss your property’s unique features to refine the revenue estimate and get a more accurate projection. We’ll also answer your questions about getting started so we can list your property immediately.</span>
                  </div>

                  <div className="faq-details">
                    <span className='que'>How will City Relay help me earn more?</span>
                    <span className='ans'>We use flexible letting and a smart pricing algorithm that automatically adjusts rates to ensure maximum earning potential. This dynamic pricing strategy will keep your property competitive in London’s private rental market.
                    </span>
                  </div>

                  <div className="faq-details">
                    <span className='que'>Does City Relay handle everything?</span>
                    <span className='ans'>Yes! We’ll take care of the whole property management process, from professional cleaning and hotel-quality linens to 24/7 guest support and maintenance. We’ll even list your property if you haven’t done it yet.
                    </span>
                  </div>

                </div>

              </div>
              {/* text below the table */}
              <div id='after-table'>
                <label style={{ marginBottom: '24px' }}>City Relay is ready to manage your property and optimise its potential</label>
                <div className="inline-flex gap-8">


                  <button onClick={handleScroll} className='btn btn--primary block leading-none flex items-center justify-center btn-no-vpad'>Book a Call Now!</button>
                  {sendToEmail && (
                    <div className="flex items-center gap-6 flex-wrap text-navy-dark font-bold text-lg">
                      {/* Phone */}
                      <div className="inline-flex items-center gap-4">
                        <div className="p-2 rounded">
                          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8.96006 2.55371C5.4328 2.55371 2.56006 5.42646 2.56006 8.95371V23.0337C2.56006 26.561 5.4328 29.4337 8.96006 29.4337H23.0401C26.5673 29.4337 29.4401 26.561 29.4401 23.0337V8.95371C29.4401 5.42646 26.5673 2.55371 23.0401 2.55371H8.96006ZM11.5238 7.70121C11.9257 7.71849 12.2948 7.94494 12.5176 8.29246C12.771 8.68798 13.1643 9.30182 13.6763 10.1025C14.1435 10.8327 14.1761 11.7759 13.7588 12.5375L12.8213 13.8712C12.5679 14.2322 12.5173 14.6942 12.6876 15.1012C12.9525 15.7348 13.4662 16.7086 14.3788 17.6212C15.2914 18.5339 16.2652 19.0475 16.8988 19.3125C17.3058 19.4827 17.7678 19.4322 18.1288 19.1787L19.4626 18.2412C20.2242 17.8239 21.1673 17.8572 21.8976 18.3237C22.6982 18.8357 23.312 19.229 23.7076 19.4825C24.0551 19.7052 24.2815 20.0743 24.2988 20.4762C24.3974 22.7572 22.6302 23.6812 22.1163 23.6812C21.7605 23.6812 17.4825 24.1674 12.6576 19.3425C7.8326 14.5175 8.31881 10.2396 8.31881 9.88371C8.31881 9.36979 9.24285 7.60265 11.5238 7.70121Z" fill="#12203A" />
                          </svg>



                        </div>
                        <span style={{ marginTop: '-20px' }}> +44 20 3865 0599</span>
                      </div>

                      {/* Email */}
                      <div className="inline-flex items-center gap-4 ml-8">
                        <div className=" p-2 rounded">
                          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8.96006 2.56006C5.4328 2.56006 2.56006 5.4328 2.56006 8.96006V23.0401C2.56006 26.5673 5.4328 29.4401 8.96006 29.4401H23.0401C26.5673 29.4401 29.4401 26.5673 29.4401 23.0401V8.96006C29.4401 5.4328 26.5673 2.56006 23.0401 2.56006H8.96006ZM8.32006 10.2401H23.6801C23.7953 10.2401 23.9039 10.2531 24.0063 10.2851L17.7151 16.5701C16.7679 17.5173 15.226 17.5173 14.2788 16.5701L7.99381 10.2851C8.09621 10.2531 8.20486 10.2401 8.32006 10.2401ZM7.08506 11.1938L11.8976 16.0001L7.08506 20.8063C7.05306 20.7039 7.04006 20.5953 7.04006 20.4801V11.5201C7.04006 11.4049 7.05306 11.2962 7.08506 11.1938ZM24.9151 11.1938C24.9471 11.2962 24.9601 11.4049 24.9601 11.5201V20.4801C24.9601 20.5953 24.9471 20.7039 24.9151 20.8063L20.0963 16.0001L24.9151 11.1938ZM12.8001 16.9026L13.3701 17.4788C14.0933 18.202 15.0466 18.5601 15.9938 18.5601C16.9474 18.5601 17.8944 18.202 18.6176 17.4788L19.1938 16.9026L24.0063 21.7151C23.9039 21.7471 23.7953 21.7601 23.6801 21.7601H8.32006C8.20486 21.7601 8.09621 21.7471 7.99381 21.7151L12.8001 16.9026Z" fill="#12203A" />
                          </svg>


                        </div>
                        <span style={{ marginTop: '-20px' }}>info@cityrelay.com</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="container">
              <h1 className="text-h1 text-center mb-12">{new_labels.heading}</h1>

              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 max-w-5xl mx-auto w-full">
                <div className="col-span-1 md:col-span-5 flex flex-col justify-center py-16 px-6 bg-navy-dark text-white">
                  {state?.estimate && (
                    <div className="space-y-3 mb-12 md:mb-16 text-center max-w-xs mx-auto">
                      <h2 className="text-h6">{new_labels.income}</h2>
                      <h3 className="text-h2">{new_labels.currency}{xValue} - {new_labels.currency}{yValue}</h3>
                      <p className="opacity-40 text-xs">{new_labels.disclaimer}</p>
                    </div>
                  )}

                  {section.assumptions && (
                    <div className="max-w-xs mx-auto">
                      <h4 className="text-h5 mb-6 text-center">{new_labels.assumptionsHeading}</h4>
                      <div className="flex flex-col justify-center">
                        <div className="text-sm space-y-3 max-w-[16rem]  mx-auto">
                          {section.assumptions.assumptions?.map((item, i) => (
                            <div key={`assumption${i}`} className="grid grid-cols-12 gap-4 items-center">
                              <div className="col-span-3">
                                <div className="aspect-w-1 aspect-h-1">
                                  <div>{item.illustration && <Image data={item.illustration} />}</div>
                                </div>
                              </div>
                              <div className="col-span-9">{new_labels.assumptions[i]}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                </div>

                <div className="col-span-1 md:col-span-7 bg-white p-10 border border-opacity-5 border-black flex flex-col">
                  {section.calendlyHeading && <h3 className="text-h4 mb-8">{new_labels.calendlyHeading}</h3>}

                  {/* if the agent is already selected OR city is not_london */}

                  {(isAgentSelected || cityName == "not_london") && <div className="bg-seafoam flex-auto p-5 flex items-center justify-center custom2">
                    <InlineWidget
                      styles={{
                        width: '100%',
                      }}
                      pageSettings={{
                        hideEventTypeDetails: true,
                        hideLandingPageDetails: true,
                      }}
                      url={widgetUrl}
                      prefill={{
                        email: state?.email || '',
                        name: state?.full_name || '',
                      }}
                    />
                  </div>}

                </div>
              </div>

            </div>
          )}
        </div>

      </section>
    </Layout >
  ) : null
}

export const pageQuery = graphql`
  query pricingResultQuery($id: String!) {
    wpPage(id: { eq: $id }) {
      title
      id
      ...SEO
      pricingResult {
        pricingResult {
          assumptions {
            heading
            assumptions {
              illustration {
                ... Image
              }
              assumption
            }
          }
          disclaimer
          heading
          calendlyHeading
        }
      }
    }
  }
`
